import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Award, Star, Zap, Briefcase, FileText, Loader2, ArrowUp, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CREDIT_RULES_DISPLAY = [
  { type: 'lead_assessed', label: 'Lead Assessed', points: '+1', icon: FileText, color: 'text-blue-600 bg-blue-50' },
  { type: 'report_generated', label: 'Quick Report Generated', points: '+2', icon: Zap, color: 'text-green-600 bg-green-50' },
  { type: 'lead_converted', label: 'Lead Converted to Case', points: '+5', icon: Star, color: 'text-violet-600 bg-violet-50' },
  { type: 'case_completed', label: 'Case Completed', points: '+10', icon: Briefcase, color: 'text-amber-600 bg-amber-50' },
];

const TIER_CONFIG = {
  'R-PACE Partner': { color: 'from-slate-500 to-slate-600', badge: 'bg-slate-100 text-slate-700 border-slate-300', icon: Award },
  'R-PACE Pro': { color: 'from-amber-500 to-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-300', icon: Star },
  'R-PACE Elite': { color: 'from-violet-500 to-violet-600', badge: 'bg-violet-50 text-violet-700 border-violet-300', icon: Zap },
};

const HISTORY_ICONS = {
  lead_assessed: { icon: FileText, color: 'text-blue-500 bg-blue-50' },
  report_generated: { icon: Zap, color: 'text-green-500 bg-green-50' },
  lead_converted: { icon: Star, color: 'text-violet-500 bg-violet-50' },
  case_completed: { icon: Briefcase, color: 'text-amber-500 bg-amber-50' },
};

export default function PartnerCreditsPage() {
  const { partner } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('rpace_token');
    fetch(`${API_URL}/api/partners/dashboard-stats`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => setStats(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!stats) return null;

  const { credits } = stats;
  const tierCfg = TIER_CONFIG[credits.tier] || TIER_CONFIG['R-PACE Partner'];
  const TierIcon = tierCfg.icon;
  const progressToNext = credits.next_tier_credits ? Math.min((credits.total / credits.next_tier_credits) * 100, 100) : 100;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="partner-credits-page">
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-[#1e3a5f]">Credits & Recognition</h1>
        <p className="text-xs text-slate-500 mt-1">Earn credits for every assessment, report, and conversion. Level up your partner status.</p>
      </div>

      {/* Tier Hero Card */}
      <Card className="mb-6 overflow-hidden" data-testid="tier-hero">
        <div className={`bg-gradient-to-r ${tierCfg.color} p-6 text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-white/70 uppercase mb-1">Your Status</p>
              <h2 className="text-2xl font-bold flex items-center gap-2" data-testid="tier-name">
                <TierIcon className="w-6 h-6" /> {credits.tier}
              </h2>
            </div>
            <div className="text-right">
              <p className="text-4xl font-bold" data-testid="total-credits">{credits.total}</p>
              <p className="text-xs text-white/70">Total Credits</p>
            </div>
          </div>
          {credits.next_tier && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-white/70 mb-1">
                <span>{credits.total} credits</span>
                <span className="flex items-center gap-1"><ArrowUp className="w-3 h-3" /> {credits.next_tier} at {credits.next_tier_credits}</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-2">
                <div className="bg-white h-2 rounded-full transition-all duration-700" style={{ width: `${progressToNext}%` }} />
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* How Credits Work */}
      <Card className="mb-6" data-testid="credit-rules">
        <CardContent className="p-5">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-4">How Credits Work</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {CREDIT_RULES_DISPLAY.map(rule => {
              const Icon = rule.icon;
              return (
                <div key={rule.type} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${rule.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-700">{rule.label}</p>
                    <p className="text-sm font-bold text-[#1e3a5f]">{rule.points}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Tier Roadmap */}
      <Card className="mb-6" data-testid="tier-roadmap">
        <CardContent className="p-5">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-4">Tier Roadmap</p>
          <div className="flex items-center gap-4">
            {[
              { name: 'R-PACE Partner', min: 0, icon: Award },
              { name: 'R-PACE Pro', min: 15, icon: Star },
              { name: 'R-PACE Elite', min: 50, icon: Zap },
            ].map((t, i) => {
              const active = credits.tier === t.name;
              const achieved = credits.total >= t.min;
              const Icon = t.icon;
              return (
                <div key={t.name} className="flex items-center flex-1">
                  <div className={`flex-1 text-center p-3 rounded-lg border-2 transition-all ${active ? 'border-[#1e3a5f] bg-[#1e3a5f]/5 ring-2 ring-[#1e3a5f]/20' : achieved ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-slate-50'}`}>
                    <Icon className={`w-5 h-5 mx-auto mb-1 ${active ? 'text-[#1e3a5f]' : achieved ? 'text-green-600' : 'text-slate-400'}`} />
                    <p className={`text-xs font-bold ${active ? 'text-[#1e3a5f]' : achieved ? 'text-green-600' : 'text-slate-400'}`}>{t.name.replace('R-PACE ', '')}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{t.min}+ credits</p>
                  </div>
                  {i < 2 && <ChevronRight className="w-4 h-4 text-slate-300 mx-1 shrink-0" />}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Credit History */}
      <Card data-testid="credit-history">
        <CardContent className="p-5">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-4">Credit History</p>
          {credits.history.length === 0 ? (
            <div className="text-center py-8">
              <Award className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-400">No credits earned yet. Start by creating a new assessment!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {credits.history.map((item, i) => {
                const config = HISTORY_ICONS[item.type] || HISTORY_ICONS.lead_assessed;
                const Icon = config.icon;
                return (
                  <div key={`item-${i}`} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100" data-testid={`credit-item-${i}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${config.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-600 truncate">{item.label}</p>
                      <p className="text-[10px] text-slate-400">
                        {item.date ? new Date(item.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                      </p>
                    </div>
                    <span className="text-sm font-bold text-green-600 shrink-0">+{item.points}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
